import { Box, FormGroup, Tooltip } from "@mui/material";
import {
    Button,
    Card,
    FormLabel,
    Grid,
    Icon,
    Modal,
    ModalTitle,
    Typography,
} from "@platform-ui/design-system";
import React, { useEffect, useState } from "react";
import { Control, Controller, FieldValues } from "react-hook-form";
import {
    DataGrid,
    GridPaginationModel,
    GridRowSelectionModel,
    GridToolbar,
} from "@mui/x-data-grid";
import { ZuoraObjectsMetaDataType } from "../AccountInfo";

interface ObjectsModelPropData {
    control: Control<FieldValues, any>;
    rows: ZuoraObjectsMetaDataType[];
    onSelectionChange: (selectedObjects: string[]) => void;
    provisionedObjectsList: string[];
    provisionCompletionStatus: boolean;
    snowflakeAlreadyInstalled: boolean;
    onSubmitObjectProvision: () => void;
}

export const ObjectsListModel = ({
    control,
    rows,
    onSelectionChange,
    provisionedObjectsList,
    provisionCompletionStatus,
    snowflakeAlreadyInstalled,
    onSubmitObjectProvision,
}: ObjectsModelPropData) => {
    const [rowSelectionModel, setRowSelectionModel] =
        useState<GridRowSelectionModel>([]);
    const [prevRowSelection, setPrevRowSelectionModel] =
        useState<GridRowSelectionModel>([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        page: 0,
        pageSize: 10,
    });

    useEffect(() => {
        const matchedRows = rows
            .filter((row) => provisionedObjectsList.includes(row.objectName))
            .map((row) => row.id);
        setRowSelectionModel(matchedRows); //Keeping the existing provisioned objects as selected
    }, [provisionedObjectsList, rows]);

    const onClose = () => {
        setRowSelectionModel(prevRowSelection);
        setModalOpen(false);
    };

    const handleRowSelectionChange = (
        newRowSelectionModel: GridRowSelectionModel
    ) => {
        // Always keep already provisioned objects selected — they cannot be deselected
        const provisionedIds = rows
            .filter((row) => provisionedObjectsList.includes(row.objectName))
            .map((row) => row.id);
        const safeSelectionModel = Array.from(
            new Set([...newRowSelectionModel, ...provisionedIds])
        );
        setRowSelectionModel(safeSelectionModel);
        const selectedObjectNames = safeSelectionModel // Map selected IDs to their objectName values
            .map((id) => rows.find((row) => row.id === id)?.objectName)
            .filter(Boolean) as string[];
        onSelectionChange(selectedObjectNames);
    };

    return (
        <Controller
            name="connector-select-objects"
            control={control}
            rules={{
                validate: () => {
                    if (rowSelectionModel.length === 0) {
                        return "This is a required field";
                    }
                    return true;
                },
            }}
            render={({ fieldState: { error } }) => (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "baseline",
                            flexDirection: "column",
                            gap: "16px",
                        }}
                    >
                        <FormLabel>
                            Billing Standard Objects
                            <span>
                                <Icon body="asterisk" fontSize="small" color="error" />
                            </span>
                            <Tooltip
                                arrow
                                placement="top-start"
                                title={
                                    <Typography
                                        variant="subtitle3"
                                        body="This is the complete list of objects supported by Zuora Secure Data Share for Snowflake. However, only objects that the tenant has permission to access will be included in the Data Share during provisioning. Custom objects will be included by default."
                                    />
                                }
                            >
                                <span>
                                    <Icon body="info_outline" fontSize="small" />
                                </span>
                            </Tooltip>
                        </FormLabel>
                        {rowSelectionModel?.length > 0 && (
                            <Typography
                                sx={{ marginRight: "1%" }}
                                variant="subtitle3"
                                body={`${rowSelectionModel.length} of ${rows.length} objects selected`}
                            />
                        )}
                        <Button
                            sx={{ marginBottom: "1%" }}
                            body={rowSelectionModel.length === 0 ? "Select" : "Reselect"}
                            dsOnClick={() => {
                                setModalOpen(true);
                                if (rowSelectionModel.length > 0) {
                                    setPrevRowSelectionModel(rowSelectionModel);
                                }
                            }}
                            disabled={snowflakeAlreadyInstalled && !provisionCompletionStatus} // Object Selection is enabled only when the provisioning is complete and the status is in SUCCESS
                            variant={rowSelectionModel.length > 0 ? "text" : "contained"}
                        />
                        {error && (
                            <Typography variant="body2" color="error">
                                {error.message}
                            </Typography>
                        )}
                    </Box>
                    <Modal
                        maxWidth="sm"
                        fullWidth
                        open={modalOpen}
                        id="object-select-modal"
                        dsOnClose={onClose}
                        header={
                            <ModalTitle
                                dsOnClose={onClose}
                                body="Select Billing Standard Objects"
                                subtitle="Billing Standard Objects supported by Snowflake"
                            />
                        }
                        body={
                            <DataGrid
                                disableRowSelectionOnClick
                                pagination
                                pageSizeOptions={[5, 10, 20]}
                                paginationModel={paginationModel}
                                onPaginationModelChange={setPaginationModel}
                                isRowSelectable={(params) => {
                                    const objectName = rows.find((row) => row.id === params.id)?.objectName;
                                    return !provisionedObjectsList.includes(objectName ?? "");
                                }}
                                sx={{
                                    ".MuiDataGrid-toolbarContainer": {
                                        display: "block",
                                    },
                                    ".MuiTablePagination-selectLabel": {
                                        margin: "0px",
                                    },
                                    ".MuiTablePagination-displayedRows": {
                                        margin: "0px",
                                    },
                                    ".MuiDataGrid-filler": {
                                        display: "none",
                                    },
                                    ".MuiDataGrid-overlayWrapper": {
                                        height: "auto !important",
                                        padding: "5px",
                                    },
                                    ".MuiDataGrid-overlayWrapperInner": {
                                        height: "auto !important",
                                    },
                                }}
                                rows={rows}
                                disableColumnFilter
                                checkboxSelection
                                disableColumnSelector
                                disableDensitySelector
                                disableColumnMenu
                                slots={{
                                    toolbar: GridToolbar,
                                    noRowsOverlay: () => (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            sx={{
                                                height: "100%",
                                                padding: "1rem",
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                color="gray"
                                                body="No Objects found for this view"
                                            />
                                        </Box>
                                    ),
                                }}
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        csvOptions: { disableToolbarButton: true },
                                        printOptions: { disableToolbarButton: true },
                                    },
                                    filterPanel: {
                                        disableAddFilterButton: true,
                                    },
                                }}
                                onRowSelectionModelChange={handleRowSelectionChange}
                                rowSelectionModel={rowSelectionModel}
                                columns={[
                                    {
                                        field: "id",
                                        headerName: "Number",
                                    },
                                    {
                                        field: "objectName",
                                        headerName: "Object Name",
                                        sortable: true,
                                        width: 200,
                                    },
                                ]}
                            />
                        }
                        footer={
                            <Card
                                id="select-all-objects"
                                body={
                                    <FormGroup>
                                        <Grid container direction="row" justify="flex-end">
                                            <Grid item>
                                                <Button
                                                    dsOnClick={(e) => {
                                                        setRowSelectionModel(prevRowSelection);
                                                        setModalOpen(false);
                                                    }}
                                                    body="Cancel"
                                                    variant="tertiary"
                                                    size="medium"
                                                />
                                            </Grid>
                                            <Grid item>
                                                <Button
                                                    dsOnClick={(e) => {
                                                        setModalOpen(false);
                                                        snowflakeAlreadyInstalled &&
                                                            onSubmitObjectProvision();
                                                    }}
                                                    disabled={
                                                        snowflakeAlreadyInstalled &&
                                                        !rowSelectionModel.some((id) => {
                                                            const objectName = rows.find((row) => row.id === id)?.objectName;
                                                            return objectName !== undefined && !provisionedObjectsList.includes(objectName);
                                                        })
                                                    }
                                                    body={snowflakeAlreadyInstalled ? "Save" : "Done"}
                                                    size="medium"
                                                />
                                            </Grid>
                                        </Grid>
                                    </FormGroup>
                                }
                            />
                        }
                    />
                </>
            )}
        />
    );
};
